/**
 * The state map's FIELD — the design system's `QuadrantMap.jsx` ported as it
 * is (Sprint 1.9.9 D-M4, P7, R42).
 *
 * ── WHAT IT DRAWS, AND WHAT IT NEVER SEES ───────────────────────────────────
 *
 * A static canvas: four corners in their original colours, blended as the
 * design system blends them (a gaussian of σ 0.4 around each corner on a
 * 120-cell grid, the jsx's brightness curve), composited over `--viz-base`.
 * The field leans gently toward ONE corner, chosen by the reading's quadrant
 * TOKEN — looked up, never derived from a sign — and is symmetric with none.
 *
 * It takes a token and a theme and nothing else, which is the whole point of
 * this file being a `clientScience` NOT_SCIENCE row rather than an exemption:
 * no score, no axis value and no confidence can reach it, so nothing drawn here
 * can be a measurement. The dot, the trail and the "your usual" ring are DOM,
 * positioned by CSS over the payload's numbers (`QuadrantMap.tsx`), and this
 * never paints a data point (e2e "the canvas draws no data point").
 *
 * Differences from the jsx, each a removal: no marker, no corner labels (the
 * words are the payload's, drawn as DOM), no animation, no crosshair or border
 * (CSS), no rounded clip (CSS). The composite is keyed on the THEME rather than
 * on the jsx's mean-brightness test of the base colour, and the backing store
 * is a fixed 512 × 512, scaled by CSS, so the pixels are the same at any width.
 */

export type FieldLean = 'act_pos_bal_pos' | 'act_pos_bal_neg' | 'act_neg_bal_neg' | 'act_neg_bal_pos' | null
export type FieldTheme = 'light' | 'dark'

interface Corner {
  /** Position in the unit square: u is balance (left to right), v is activation (top is high). */
  u: number
  v: number
  rgb: readonly [number, number, number]
}

/**
 * The jsx's `CORNERS`, keyed by the package's quadrant token instead of the
 * decoder's four words: activation up, balance right. The RGBs are the design
 * system's literal corner colours (D-M4), never the `--q-*` tokens.
 */
export const CORNER_OF_TOKEN: Readonly<Record<Exclude<FieldLean, null>, Corner>> = {
  act_pos_bal_pos: { u: 1, v: 0, rgb: [0, 212, 255] },
  act_pos_bal_neg: { u: 0, v: 0, rgb: [236, 72, 153] },
  act_neg_bal_neg: { u: 0, v: 1, rgb: [139, 92, 246] },
  act_neg_bal_pos: { u: 1, v: 1, rgb: [61, 220, 151] },
}

const TOKENS = Object.keys(CORNER_OF_TOKEN) as Array<Exclude<FieldLean, null>>

/** The concept's weights: even with no lean, and one corner a little heavier with one. */
const EVEN = 0.25
const LEAN_ON = 0.34
const LEAN_OFF = 0.22

/** The backing store, fixed; CSS scales it. */
export const FIELD_SIZE = 512
const GRID = 120
const SIGMA = 0.4

/** The four corners' weights, normalised, in `TOKENS` order. */
export function fieldWeights(lean: FieldLean): number[] {
  const raw = TOKENS.map((t) => (lean === null ? EVEN : t === lean ? LEAN_ON : LEAN_OFF))
  const sum = raw.reduce((a, b) => a + b, 0)
  return raw.map((w) => w / sum)
}

function baseOf(canvas: HTMLCanvasElement): [number, number, number] {
  const raw = getComputedStyle(canvas).getPropertyValue('--viz-base').trim()
  const parts = raw.split(',').map((n) => Number.parseFloat(n))
  return parts.length === 3 && parts.every((n) => Number.isFinite(n))
    ? [parts[0], parts[1], parts[2]]
    : [7, 10, 18]
}

/**
 * Paint the field. Returns whether it painted: `false` where there is no 2D
 * context at all (jsdom), which the caller reports as "not painted" rather than
 * as a field.
 */
export function paintQuadrantField(canvas: HTMLCanvasElement, lean: FieldLean, theme: FieldTheme): boolean {
  canvas.width = FIELD_SIZE
  canvas.height = FIELD_SIZE
  const ctx = canvas.getContext('2d')
  if (ctx === null) return false
  const off = document.createElement('canvas')
  off.width = GRID
  off.height = GRID
  const octx = off.getContext('2d')
  if (octx === null) return false

  const probs = fieldWeights(lean)
  const corners = TOKENS.map((t) => CORNER_OF_TOKEN[t])
  const denom = 2 * SIGMA * SIGMA
  const img = octx.createImageData(GRID, GRID)
  const data = img.data
  for (let py = 0; py < GRID; py++) {
    const v = py / (GRID - 1)
    for (let px = 0; px < GRID; px++) {
      const u = px / (GRID - 1)
      let wsum = 0
      let r = 0
      let g = 0
      let b = 0
      for (let k = 0; k < corners.length; k++) {
        const c = corners[k]
        const du = u - c.u
        const dv = v - c.v
        const w = probs[k] * Math.exp(-(du * du + dv * dv) / denom)
        wsum += w
        r += c.rgb[0] * w
        g += c.rgb[1] * w
        b += c.rgb[2] * w
      }
      const i = (py * GRID + px) * 4
      if (wsum < 1e-4) {
        data[i + 3] = 0
        continue
      }
      const bright = Math.min(1, wsum / 0.5)
      data[i] = r / wsum
      data[i + 1] = g / wsum
      data[i + 2] = b / wsum
      data[i + 3] = Math.round(Math.pow(bright, 0.85) * 255)
    }
  }
  octx.putImageData(img, 0, 0)

  const [br, bg, bb] = baseOf(canvas)
  ctx.clearRect(0, 0, FIELD_SIZE, FIELD_SIZE)
  ctx.fillStyle = `rgb(${br}, ${bg}, ${bb})`
  ctx.fillRect(0, 0, FIELD_SIZE, FIELD_SIZE)
  ctx.imageSmoothingEnabled = true
  // A glow on navy in dark, a pastel on white in light: the jsx's two
  // composites, chosen by the theme rather than by measuring the base colour.
  ctx.globalCompositeOperation = theme === 'light' ? 'multiply' : 'screen'
  ctx.drawImage(off, 0, 0, FIELD_SIZE, FIELD_SIZE)
  ctx.globalCompositeOperation = 'source-over'
  return true
}
